import React, { useMemo, type ReactNode } from 'react';
import { graphql, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import { Flex } from '@atlaskit/primitives/compiled';
import { useIntl } from '@atlassian/jira-intl';
import FilterRefinement from '@atlassian/jira-filter-refinement/src/ui/FilterRefinement.tsx';
import type { Props as FilterRefinementProps } from '@atlassian/jira-filter-refinement/src/ui/types.tsx';
import type { JQLBuilderWithAIFilterRefinementQuery } from './__generated__/JQLBuilderWithAIFilterRefinementQuery.graphql';
import messages from './messages.tsx';

type Props = Pick<
	FilterRefinementProps,
	'defaultJql' | 'onReset' | 'onSearch' | 'projectContext'
> & {
	beforeControls?: ReactNode;
	filterRefinementQueryReference: PreloadedQuery<JQLBuilderWithAIFilterRefinementQuery>;
};

export const JQLBuilderWithAIFilterRefinement = ({
	defaultJql,
	onSearch,
	beforeControls,
	filterRefinementQueryReference,
	projectContext,
	onReset,
}: Props): React.JSX.Element => {
	const filterRefinementData = usePreloadedQuery<JQLBuilderWithAIFilterRefinementQuery>(
		graphql`
			query JQLBuilderWithAIFilterRefinementQuery(
				$cloudId: ID!
				$isFilterRefinementEnabled: Boolean!
				$scope: JiraJqlScopeInput
			) {
				jira {
					...FilterRefinement
						@arguments(assignees: 50, cloudId: $cloudId, scope: $scope)
						@include(if: $isFilterRefinementEnabled)
				}
			}
		`,
		filterRefinementQueryReference,
	);

	const { formatMessage } = useIntl();

	const scope = useMemo(() => {
		return projectContext
			? {
					project: {
						projectKey: projectContext,
					},
				}
			: undefined;
	}, [projectContext]);

	return (
		<Flex gap="space.075" justifyContent="start" alignItems="center">
			{beforeControls}
			<FilterRefinement
				defaultJql={defaultJql}
				fragmentRef={filterRefinementData.jira}
				onSearch={onSearch}
				searchFieldPlaceholder={formatMessage(messages.searchWork)}
				isModeSwitcherEnabled
				isResponsive={false}
				projectContext={projectContext}
				onReset={onReset}
				scope={scope}
			/>
		</Flex>
	);
};
